// src/components/Footer.js
import React from 'react';
import { useLanguage } from '../LanguageContext.js';
import { Mail, Phone, Download } from 'lucide-react';

const Footer = () => {
  const { language, t, toggleLanguage } = useLanguage();

  const scrollToSection = (sectionId) => {
    const section = document.getElementById(sectionId);
    if (section) {
      section.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const year = new Date().getFullYear();

  return (
    <footer style={{ backgroundColor: '#000000' }} className="relative overflow-hidden">
      <div className="container mx-auto px-4 md:px-6 py-12 md:py-16 relative z-10">
        
        {/* Main content */}
        <div className="max-w-6xl mx-auto">
          
          {/* Top section - Logo + Description */}
          <div className="mb-12 md:mb-16 pb-12 md:pb-16" style={{ borderBottom: '1px solid #333333' }}>
            <div className="flex flex-col md:flex-row gap-8 md:gap-12 items-start">
              {/* Logo Square */}
              <div className="flex-shrink-0">
                <img
                  src="/images/DHOME_Logo_Square.webp"
                  alt="DHOME"
                  className="w-20 h-20 md:w-24 md:h-24 object-contain"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </div>
              {/* Description + Visura */}
              <div className="flex-1">
                <p className="text-xs md:text-sm leading-relaxed mb-6" style={{ color: '#B0B0B0', fontWeight: 300 }}>
                  {t('footer', 'company_description')}
                </p>
                {/* Visura della Camera di Commercio */}
                <div className="flex items-center gap-3 pt-4" style={{ borderTop: '1px solid #333333' }}>
                  <a 
                    href="/documents/visura-camera-commercio.pdf" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-xs md:text-sm transition-colors duration-300"
                    style={{ color: '#B0B0B0' }}
                    onMouseEnter={(e) => e.currentTarget.style.color = '#FFFFFF'}
                    onMouseLeave={(e) => e.currentTarget.style.color = '#B0B0B0'}
                  >
                    <Download size={14} style={{ flexShrink: 0 }} />
                    <span>{t('footer', 'business_registration')}</span>
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-12">
            
            {/* Navigation */}
            <div>
              <p className="text-xs uppercase tracking-widest font-medium mb-6" style={{ color: '#585856' }}>
                {t('footer', 'navigate')}
              </p>
              <div className="space-y-3">
                {[
                  { id: 'home', label: t('footer', 'nav_home') },
                  { id: 'about', label: t('footer', 'nav_about') },
                  { id: 'contact', label: t('footer', 'nav_contact') }
                ].map((item, index) => (
                  <button 
                    key={index}
                    onClick={() => scrollToSection(item.id)} 
                    className="block text-xs md:text-sm transition-colors duration-300"
                    style={{ color: '#B0B0B0' }}
                    onMouseEnter={(e) => e.currentTarget.style.color = '#FFFFFF'}
                    onMouseLeave={(e) => e.currentTarget.style.color = '#B0B0B0'}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Contact */}
            <div>
              <p className="text-xs uppercase tracking-widest font-medium mb-6" style={{ color: '#585856' }}>
                {t('footer', 'get_in_touch')}
              </p>
              <div className="space-y-4">
                <a 
                  href="mailto:info@dhome-re.eu" 
                  className="flex items-start gap-3 text-xs md:text-sm transition-colors duration-300 group"
                  style={{ color: '#B0B0B0' }}
                  onMouseEnter={(e) => e.currentTarget.style.color = '#FFFFFF'}
                  onMouseLeave={(e) => e.currentTarget.style.color = '#B0B0B0'}
                >
                  <Mail size={16} style={{ color: '#585856', flexShrink: 0, marginTop: '2px' }} />
                  <span>info@dhome-re.eu</span>
                </a>
                <a 
                  href="tel:+393461343029" 
                  className="flex items-start gap-3 text-xs md:text-sm transition-colors duration-300 group"
                  style={{ color: '#B0B0B0' }}
                  onMouseEnter={(e) => e.currentTarget.style.color = '#FFFFFF'}
                  onMouseLeave={(e) => e.currentTarget.style.color = '#B0B0B0'}
                >
                  <Phone size={16} style={{ color: '#585856', flexShrink: 0, marginTop: '2px' }} />
                  <span>+39 3461 343029</span>
                </a>
              </div>
            </div>

            {/* Privacy */}
            <div>
              <p className="text-xs uppercase tracking-widest font-medium mb-6" style={{ color: '#585856' }}>
                {t('footer', 'legal')}
              </p>
              <div className="space-y-3">
                <a 
                  href="https://www.iubenda.com/privacy-policy/YOUR-IUBENDA-ID/terms-and-conditions" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="block text-xs md:text-sm transition-colors duration-300"
                  style={{ color: '#B0B0B0' }}
                  onMouseEnter={(e) => e.currentTarget.style.color = '#FFFFFF'}
                  onMouseLeave={(e) => e.currentTarget.style.color = '#B0B0B0'}
                >
                  {t('footer', 'terms_conditions')}
                </a>
                <a 
                  href="https://www.iubenda.com/privacy-policy/YOUR-IUBENDA-ID" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="block text-xs md:text-sm transition-colors duration-300"
                  style={{ color: '#B0B0B0' }}
                  onMouseEnter={(e) => e.currentTarget.style.color = '#FFFFFF'}
                  onMouseLeave={(e) => e.currentTarget.style.color = '#B0B0B0'}
                >
                  {t('footer', 'privacy_policy')}
                </a>
                <a 
                  href="https://www.iubenda.com/privacy-policy/YOUR-IUBENDA-ID/cookie-policy" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="block text-xs md:text-sm transition-colors duration-300"
                  style={{ color: '#B0B0B0' }}
                  onMouseEnter={(e) => e.currentTarget.style.color = '#FFFFFF'}
                  onMouseLeave={(e) => e.currentTarget.style.color = '#B0B0B0'}
                >
                  {t('footer', 'cookie_policy')}
                </a>
              </div>
            </div>

            {/* Language */}
            <div>
              <p className="text-xs uppercase tracking-widest font-medium mb-6" style={{ color: '#585856' }}>
                {t('footer', 'language')}
              </p>
              <div className="flex gap-3">
                <button 
                  onClick={language === 'en' ? toggleLanguage : undefined}
                  className="text-xs md:text-sm font-medium transition-colors duration-300"
                  style={{ 
                    color: language === 'it' ? '#FFFFFF' : '#585856',
                    cursor: language === 'it' ? 'default' : 'pointer'
                  }}
                  onMouseEnter={(e) => language !== 'it' && (e.currentTarget.style.color = '#B0B0B0')}
                  onMouseLeave={(e) => e.currentTarget.style.color = language === 'it' ? '#FFFFFF' : '#585856'}
                >
                  IT
                </button>
                <span style={{ color: '#333333' }}>/</span>
                <button 
                  onClick={language === 'it' ? toggleLanguage : undefined}
                  className="text-xs md:text-sm font-medium transition-colors duration-300"
                  style={{ 
                    color: language === 'en' ? '#FFFFFF' : '#585856',
                    cursor: language === 'en' ? 'default' : 'pointer'
                  }}
                  onMouseEnter={(e) => language !== 'en' && (e.currentTarget.style.color = '#B0B0B0')}
                  onMouseLeave={(e) => e.currentTarget.style.color = language === 'en' ? '#FFFFFF' : '#585856'}
                >
                  EN
                </button>
              </div>
            </div>
          </div>

          {/* Divider */}
          <div style={{ height: '1px', backgroundColor: '#333333', margin: '16px 0' }}></div>

          {/* Copyright */}
          <div className="flex flex-col sm:flex-row justify-center sm:justify-between items-center gap-4 text-xs" style={{ color: '#585856' }}>
            <p>
              © {year} DHOME Real Estate. {t('footer', 'all_rights_reserved')}
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;